import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useContentToken, useSlotGroup, useCollectionBinding, usePropertyField, usePropertyAddressUrl, useTokens } from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import {
  repliWhiteLogoUrl,
  MultihubLogoMonoWhite,
  FacebookIcon,
  InstagramIcon,
  YouTubeIcon,
  FairHousingIcon,
  PetsAllowedIcon,
  WheelchairIcon,
} from '@vibesites/assets';

// ============================================================================
// Types
// ============================================================================

interface OfficeHour {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

function hasValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const normalized = val.trim().toLowerCase();
  return normalized !== '' && normalized !== 'false' && normalized !== 'null' && normalized !== 'undefined';
}

// ============================================================================
// Luminance / Contrast Helpers
// ============================================================================

/** Parse a 6-digit hex string into RGB components. */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const cleaned = hex.replace('#', '');
  if (cleaned.length !== 6) return null;
  return {
    r: parseInt(cleaned.slice(0, 2), 16),
    g: parseInt(cleaned.slice(2, 4), 16),
    b: parseInt(cleaned.slice(4, 6), 16),
  };
}

/**
 * Calculate WCAG relative luminance of a color.
 * https://www.w3.org/TR/WCAG20/#relativeluminancedef
 */
function getRelativeLuminance(hex: string): number {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;

  const [rs, gs, bs] = [rgb.r / 255, rgb.g / 255, rgb.b / 255].map((c) =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  );

  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Determine the best contrasting mono color (white or dark) for a given
 * background color. Uses WCAG luminance threshold.
 * Favors white on medium tones; flips to dark only on genuinely light
 * backgrounds (pastels, cream, near-white).
 */
function getContrastLogoColor(bgHex: string): '#ffffff' | '#1a1a1a' {
  return getRelativeLuminance(bgHex) > 0.55 ? '#1a1a1a' : '#ffffff';
}

/** Formats a phone string into (XXX) XXX-XXXX. Passes through if already formatted or unrecognizable. */
function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  // Strip leading country code "1" if 11 digits
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

/** Returns a digits-only (with optional leading +) string for use in tel: links,
 *  or null when the value is empty/missing. */
function cleanPhoneForTel(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const cleaned = raw.replace(/[^+\d]/g, '');
  return cleaned || null;
}

function formatTime(raw: string): string {
  const trimmed = raw.trim();
  // Already has am/pm — just normalize casing
  if (/[ap]m/i.test(trimmed)) {
    return trimmed.replace(/([ap]m)/i, (m) => m.toLowerCase());
  }
  // Parse 24h "HH:MM" or "H:MM"
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return trimmed;

  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'pm' : 'am';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${hours}:${minutes} ${period}`;
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS: Record<string, string> = {
  mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday',
  fri: 'Friday', sat: 'Saturday', sun: 'Sunday',
};

interface GroupedHours {
  label: string;
  hours: string;
}

function normalizeDay(day: string): string {
  return day.trim().toLowerCase().slice(0, 3);
}

function groupOfficeHours(raw: OfficeHour[]): GroupedHours[] {
  // Index the provided entries by canonical day key so we can fill in any
  // days the property did not supply. Missing days fall back to "Closed".
  const byDay = new Map<string, OfficeHour>();
  for (const entry of raw) {
    if (!hasValue(entry.day)) continue;
    const key = normalizeDay(entry.day);
    if (!DAY_LABELS[key]) continue;
    // First entry wins if duplicates are provided.
    if (!byDay.has(key)) byDay.set(key, entry);
  }

  const groups: GroupedHours[] = [];

  for (const key of DAY_ORDER) {
    const entry = byDay.get(key);
    const dayLabel = DAY_LABELS[key];
    const isClosed =
      !entry || (!hasValue(entry.open_time) && !hasValue(entry.close_time));
    const hoursStr = isClosed
      ? 'Closed'
      : `${formatTime(entry!.open_time || '')} - ${formatTime(entry!.close_time || '')}`;

    const prev = groups[groups.length - 1];
    if (prev && prev.hours === hoursStr) {
      // Extend the previous group's range
      const dash = prev.label.indexOf(' - ');
      const startLabel = dash !== -1 ? prev.label.slice(0, dash) : prev.label;
      prev.label = `${startLabel} - ${dayLabel}`;
    } else {
      groups.push({ label: dayLabel, hours: hoursStr });
    }
  }

  return groups;
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Footer Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_mono_white_logo', suggestedFields: ['property_mono_white_logo', 'property_secondary_logo', 'property_primary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const managedByLogoDef: SlotGroupDefinition = {
  label: 'Managed By Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_company_mono_white_logo', suggestedFields: ['property_company_mono_white_logo', 'property_company_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Property Management Company Logo' },
    url: { label: 'URL', type: 'url', defaultField: 'property_company_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const facebookLinkDef: SlotGroupDefinition = {
  label: 'Facebook Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_facebook_url', suggestedFields: ['property_facebook_url'] },
    label: { label: 'Label', type: 'text', defaultValue: 'Facebook' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const instagramLinkDef: SlotGroupDefinition = {
  label: 'Instagram Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_instagram_url', suggestedFields: ['property_instagram_url'] },
    label: { label: 'Label', type: 'text', defaultValue: 'Instagram' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const youtubeLinkDef: SlotGroupDefinition = {
  label: 'YouTube Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_youtube_url', suggestedFields: ['property_youtube_url'] },
    label: { label: 'Label', type: 'text', defaultValue: 'YouTube' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const poweredByLogoDef: SlotGroupDefinition = {
  label: 'Powered By Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Multihub' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://multihub.ai' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const designByLogoDef: SlotGroupDefinition = {
  label: 'Website Design By Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Agency' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://repli.com' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const weidnerCaresLogoDef: SlotGroupDefinition = {
  label: 'Weidner Cares Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Weidner Cares' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.weidner.com/weidnercares' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const loveNotesLogoDef: SlotGroupDefinition = {
  label: 'Love Notes Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Love Notes' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.weidner.com/lovenotes' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const textUsLinkDef: SlotGroupDefinition = {
  label: 'Text Us Link',
  fields: {
    text: { label: 'Text', type: 'text', defaultValue: 'Text Us' },
    url: { label: 'URL', type: 'url', defaultField: 'custom_text_us_url', suggestedFields: ['custom_text_us_url'] },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Component
// ============================================================================

export function Footer() {
  const phone = useContentToken('footer.phone');
  const phoneForTel = cleanPhoneForTel(phone);
  const addressLine1 = useContentToken('footer.address_line1');
  const managedByText = useContentToken('footer.managed_by_text');
  const poweredByText = useContentToken('footer.powered_by_text');
  const designByText = useContentToken('footer.design_by_text');

  // Always render the current year alongside the editable copyright richtext
  // so the copyright line never falls out of sync.
  const currentYear = new Date().getFullYear();

  // Address link URL resolves through usePropertyAddressUrl(), which falls back
  // through property_google_place_url -> property_google_business_url ->
  // property_full_address -> lat/lon. Final '#' guards against an empty value.
  const propertyAddressUrl = usePropertyAddressUrl();
  const city = usePropertyField('property_address_city') as string | null;
  const state = usePropertyField('property_address_state') as string | null;
  const zip = usePropertyField('property_address_zip') as string | null;

  const addressLine2 = [city, state].filter(Boolean).join(', ') + (zip ? ` ${zip}` : '');

  // Footer background is a content token (Content tab → Background Color).
  // Theme-component designSchema is not surfaced in the editor Settings UI, so
  // this has to live on contentSchema. Empty/unset falls back to the site text
  // color (same default the footer used before).
  // Derive which mono logo variant to use from that background. On dark footers
  // we default the logo to the property's mono-white asset; on genuinely light
  // footers we flip to the mono-black asset. Both are already flat single-color
  // logos, so we do NOT apply any CSS recoloring — we just point at the right
  // pre-made variant.
  const designTokens = useTokens();
  const footerBgToken = useContentToken('footer.background_color');
  const footerBgHex = (footerBgToken && footerBgToken.trim()) || designTokens?.color?.text || '#1a1a1a';
  const logoColor = getContrastLogoColor(footerBgHex);
  const useWhiteLogo = logoColor === '#ffffff';
  const logoField = useWhiteLogo ? 'property_mono_white_logo' : 'property_mono_black_logo';

  // Resolve the logo slot definition against the background-appropriate mono
  // variant. The static logoSlotDef (used by the content schema / editor)
  // keeps mono-white as its documented baseline.
  const resolvedLogoSlotDef: SlotGroupDefinition = {
    ...logoSlotDef,
    fields: {
      ...logoSlotDef.fields,
      image: {
        ...logoSlotDef.fields.image,
        defaultField: logoField,
        suggestedFields: useWhiteLogo
          ? ['property_mono_white_logo', 'property_mono_black_logo', 'property_secondary_logo', 'property_primary_logo']
          : ['property_mono_black_logo', 'property_mono_white_logo', 'property_secondary_logo', 'property_primary_logo'],
      },
    },
  };

  const logo = useSlotGroup('footer.logo', resolvedLogoSlotDef);
  const managedByLogo = useSlotGroup('footer.managed_by_logo', managedByLogoDef);
  const poweredByLogo = useSlotGroup('footer.powered_by_logo', poweredByLogoDef);
  const designByLogo = useSlotGroup('footer.design_by_logo', designByLogoDef);
  const facebookLink = useSlotGroup('footer.facebook_link', facebookLinkDef);
  const instagramLink = useSlotGroup('footer.instagram_link', instagramLinkDef);
  const youtubeLink = useSlotGroup('footer.youtube_link', youtubeLinkDef);
  const weidnerCaresLogo = useSlotGroup('footer.weidner_cares_logo', weidnerCaresLogoDef);
  const loveNotesLogo = useSlotGroup('footer.love_notes_logo', loveNotesLogoDef);
  const textUsLink = useSlotGroup('footer.text_us_link', textUsLinkDef);

  const officeHours = useCollectionBinding<OfficeHour>('footer.office_hours', {
    collection: 'office_hours',
  });

  const groupedHours = officeHours && officeHours.length > 0
    ? groupOfficeHours(officeHours)
    : [];

  return (
    <footer
      className="footer-root text-white"
      style={{ ['--footer-background' as string]: footerBgHex }}
    >
      {/* Visually hidden — exposes Background Color in the Content editor panel */}
      <EditableToken path="footer.background_color" as="div" className="hidden">
        {footerBgHex}
      </EditableToken>

      {/* ---- Main Footer Content ---- */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12">
          {/* Column 1: Logo + Social */}
          <div className="flex flex-col items-center md:items-start">
            <EditableSlotGroup groupPath="footer.logo" definition={resolvedLogoSlotDef}>
              <a href="/" className="block mb-6">
                {logo.image ? (
                  <img
                    src={logo.image}
                    alt={logo.alt || 'Community Logo'}
                    className="h-20 w-auto max-w-[200px] object-contain"
                  />
                ) : (
                  <div className="font-heading text-2xl" style={{ color: logoColor }}>Our Community</div>
                )}
              </a>
            </EditableSlotGroup>

            <div className="flex items-center gap-4">
              {facebookLink.url && (
                <EditableSlotGroup groupPath="footer.facebook_link" definition={facebookLinkDef}>
                  <a href={facebookLink.url} target={facebookLink._target || '_blank'} rel="noopener noreferrer" aria-label="Facebook" className="text-white/60 hover:text-white transition-colors">
                    <FacebookIcon size={20} />
                  </a>
                </EditableSlotGroup>
              )}
              {instagramLink.url && (
                <EditableSlotGroup groupPath="footer.instagram_link" definition={instagramLinkDef}>
                  <a href={instagramLink.url} target={instagramLink._target || '_blank'} rel="noopener noreferrer" aria-label="Instagram" className="text-white/60 hover:text-white transition-colors">
                    <InstagramIcon size={20} />
                  </a>
                </EditableSlotGroup>
              )}
              {youtubeLink.url && (
                <EditableSlotGroup groupPath="footer.youtube_link" definition={youtubeLinkDef}>
                  <a href={youtubeLink.url} target={youtubeLink._target || '_blank'} rel="noopener noreferrer" aria-label="YouTube" className="text-white/60 hover:text-white transition-colors">
                    <YouTubeIcon size={20} />
                  </a>
                </EditableSlotGroup>
              )}
            </div>

            {/* Weidner Cares + Love Notes program logos */}
            <div className="mt-5 flex items-center justify-center md:justify-start gap-6">
              <EditableSlotGroup groupPath="footer.weidner_cares_logo" definition={weidnerCaresLogoDef}>
                <a
                  href={weidnerCaresLogo.url || 'https://www.weidner.com/weidnercares'}
                  target={weidnerCaresLogo._target || '_blank'}
                  rel="noopener noreferrer"
                  aria-label="Weidner Cares"
                  className="footer-program-logo"
                >
                  {weidnerCaresLogo.image ? (
                    <VibesitesImage
                      url={weidnerCaresLogo.image}
                      alt={weidnerCaresLogo.alt || 'Weidner Cares'}
                      className="h-10 w-auto max-w-[120px] object-contain"
                    />
                  ) : null}
                </a>
              </EditableSlotGroup>

              <EditableSlotGroup groupPath="footer.love_notes_logo" definition={loveNotesLogoDef}>
                <a
                  href={loveNotesLogo.url || 'https://www.weidner.com/lovenotes'}
                  target={loveNotesLogo._target || '_blank'}
                  rel="noopener noreferrer"
                  aria-label="Love Notes"
                  className="footer-program-logo"
                >
                  {loveNotesLogo.image ? (
                    <VibesitesImage
                      url={loveNotesLogo.image}
                      alt={loveNotesLogo.alt || 'Love Notes'}
                      className="h-10 w-auto max-w-[120px] object-contain"
                    />
                  ) : null}
                </a>
              </EditableSlotGroup>
            </div>
          </div>

          {/* Column 2: Contact Info */}
          <div className="flex flex-col items-center md:items-start gap-5">
            {/* Phone */}
            <div>
              <p className="font-body text-[11px] uppercase tracking-[0.15em] text-white/80 mb-1 text-center md:text-left">Contact Us</p>
              <div className="flex items-center gap-3 justify-center md:justify-start">
                <a
                  href={phoneForTel ? `tel:${phoneForTel}` : undefined}
                  className="font-body text-sm text-white/80 hover:text-white transition-colors"
                >
                  <EditableToken path="footer.phone" defaultField="property_phone" defaultValue="(555) 555-5555" as="span">
                    {formatPhone(phone)}
                  </EditableToken>
                </a>
                {textUsLink.url && (
                  <>
                    <span aria-hidden="true" className="text-white/30">|</span>
                    <EditableSlotGroup groupPath="footer.text_us_link" definition={textUsLinkDef}>
                      <a
                        href={textUsLink.url}
                        target={textUsLink._target || '_self'}
                        rel={textUsLink._target === '_blank' ? 'noopener noreferrer' : undefined}
                        className="inline-flex items-center gap-1.5 font-body text-sm text-white/80 hover:text-white transition-colors"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                        </svg>
                        <span>{textUsLink.text || 'Text Us'}</span>
                      </a>
                    </EditableSlotGroup>
                  </>
                )}
              </div>
            </div>

            {/* Address */}
            <div>
              <p className="font-body text-[11px] uppercase tracking-[0.15em] text-white/80 mb-1 text-center md:text-left">Visit Us</p>
              <a
                href={propertyAddressUrl || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="font-body text-sm text-white/80 hover:text-white transition-colors block text-center md:text-left"
              >
                <EditableToken path="footer.address_line1" defaultField="property_address_1" defaultValue="123 Main Street" as="span" className="block">
                  {addressLine1}
                </EditableToken>
                {addressLine2 && (
                  <span className="block">{addressLine2}</span>
                )}
              </a>
            </div>
          </div>

          {/* Column 3: Office Hours */}
          <div className="flex flex-col items-center md:items-start">
            <p className="font-body text-[11px] uppercase tracking-[0.15em] text-white/80 mb-3 text-center md:text-left">Office Hours</p>
            {groupedHours.length > 0 ? (
              <dl className="space-y-2 w-full max-w-[320px]">
                {groupedHours.map((group, index) => (
                  <div key={index} className="flex justify-between font-body text-sm text-white/80">
                    <dt>{group.label}</dt>
                    <dd className="text-right">{group.hours}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="font-body text-sm text-white/50">Office hours will be displayed here.</p>
            )}
          </div>
        </div>
      </div>

      {/* ---- Compliance Icons + Legal Links + Copyright ---- */}
      <div className="border-t border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col items-center gap-4">
            <div className="flex items-center gap-6 text-white/50">
              <FairHousingIcon size={32} color="currentColor" />
              <PetsAllowedIcon size={32} color="currentColor" />
              <WheelchairIcon size={32} color="currentColor" />
            </div>
            <nav className="flex items-center gap-2 font-body text-xs text-white/50" aria-label="Legal links">
              <a href="/accessibility" className="hover:text-white/80 transition-colors">Accessibility</a>
              <span aria-hidden="true">/</span>
              <a href="/privacy-policy" className="hover:text-white/80 transition-colors">Privacy Policy</a>
              <span aria-hidden="true">/</span>
              <a href="/sitemap" className="hover:text-white/80 transition-colors">Sitemap</a>
            </nav>
            <p className="font-body text-xs text-white/40 text-center">
              © {currentYear}{' '}
              <RichTextContent path="footer.copyright" as="span" />
            </p>
          </div>
        </div>
      </div>

      {/* ---- Bottom Attribution Bar ---- */}
      <div className="bg-black">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-5 pb-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Managed By — left aligned */}
            <div className="flex flex-col items-center md:items-start gap-1.5">
              <EditableToken path="footer.managed_by_text" defaultValue="Professionally Managed by" as="span" className="font-body text-[10px] text-white/40">
                {managedByText}
              </EditableToken>
              <EditableSlotGroup groupPath="footer.managed_by_logo" definition={managedByLogoDef}>
                {managedByLogo.image ? (
                  <a href={managedByLogo.url || '#'} target={managedByLogo._target || '_blank'} rel="noopener noreferrer">
                    <img
                      src={managedByLogo.image}
                      alt={managedByLogo.alt || 'Property Management Company'}
                      className="h-8 w-auto max-w-[160px] object-contain opacity-80 hover:opacity-100 transition-opacity"
                    />
                  </a>
                ) : null}
              </EditableSlotGroup>
            </div>

            {/* Powered By — center aligned */}
            <EditableSlotGroup groupPath="footer.powered_by_logo" definition={poweredByLogoDef}>
              <div className="flex flex-col items-center gap-1.5">
                <EditableToken path="footer.powered_by_text" defaultValue="Powered by" as="span" className="font-body text-[10px] text-white/40">
                  {poweredByText}
                </EditableToken>
                {poweredByLogo.image ? (
                  <a href={poweredByLogo.url || 'https://multihub.ai'} target={poweredByLogo._target || '_blank'} rel="noopener noreferrer" className="opacity-70 hover:opacity-100 transition-opacity">
                    <img src={poweredByLogo.image} alt={poweredByLogo.alt || 'Multihub'} className="h-8 w-auto max-w-[160px] object-contain" style={{ filter: 'brightness(0) invert(1)' }} />
                  </a>
                ) : (
                  <a href="https://multihub.ai" target="_blank" rel="noopener noreferrer" className="opacity-80 hover:opacity-100 transition-opacity">
                    <MultihubLogoMonoWhite height={32} alt="Multihub" />
                  </a>
                )}
              </div>
            </EditableSlotGroup>

            {/* Design By — right aligned */}
            <EditableSlotGroup groupPath="footer.design_by_logo" definition={designByLogoDef}>
              <div className="flex flex-col items-center md:items-end gap-1.5">
                <EditableToken path="footer.design_by_text" defaultValue="Website Design by" as="span" className="font-body text-[10px] text-white/40">
                  {designByText}
                </EditableToken>
                {designByLogo.image ? (
                  <a href={designByLogo.url || 'https://repli.com'} target={designByLogo._target || '_blank'} rel="noopener noreferrer" className="opacity-70 hover:opacity-100 transition-opacity">
                    <img src={designByLogo.image} alt={designByLogo.alt || 'Agency'} className="h-8 w-auto max-w-[160px] object-contain" />
                  </a>
                ) : (
                  <a href="https://repli.com" target="_blank" rel="noopener noreferrer" className="opacity-80 hover:opacity-100 transition-opacity">
                    <img src={repliWhiteLogoUrl} alt="Repli" className="h-8 w-auto" />
                  </a>
                )}
              </div>
            </EditableSlotGroup>
          </div>
        </div>
      </div>

      <style>{`
        .footer-root {
          background: linear-gradient(
            180deg,
            color-mix(in srgb, var(--footer-background, var(--vibe-color-text)) 90%, var(--vibe-color-primary)) 0%,
            var(--footer-background, var(--vibe-color-text)) 100%
          );
        }
        .footer-program-logo {
          display: inline-flex;
          opacity: 1;
          transition: opacity 0.25s ease;
        }
        .footer-program-logo:hover {
          filter: brightness(1.08);
        }
      `}</style>
    </footer>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Footer',
  displayName: 'Footer',
  tokens: {
    'footer.background_color': {
      label: 'Background Color',
      type: 'text',
      description: 'Hex color for the footer background (e.g. #1a1a1a). Defaults to the property font color.',
      defaultField: 'property_font_color',
      suggestedFields: ['property_font_color', 'property_primary_color', 'property_secondary_color'],
      defaultValue: '#1a1a1a',
    },
    'footer.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_phone',
      defaultValue: '(555) 555-5555',
    },
    'footer.address_line1': {
      label: 'Street Address',
      type: 'text',
      defaultField: 'property_address_1',
      defaultValue: '123 Main Street',
    },
    'footer.managed_by_text': {
      label: 'Managed By Text',
      type: 'text',
      defaultValue: 'Professionally Managed by',
    },
    'footer.powered_by_text': {
      label: 'Powered By Text',
      type: 'text',
      defaultValue: 'Powered by',
    },
    'footer.design_by_text': {
      label: 'Website Design By Text',
      type: 'text',
      defaultValue: 'Website Design by',
    },
    'footer.copyright': {
      label: 'Copyright',
      type: 'richtext',
      defaultValue:
        'All Rights Reserved. <span data-type="field-token" data-field="property_name"></span>',
    },
  },
  slotGroups: {
    'footer.logo': logoSlotDef,
    'footer.managed_by_logo': managedByLogoDef,
    'footer.powered_by_logo': poweredByLogoDef,
    'footer.design_by_logo': designByLogoDef,
    'footer.facebook_link': facebookLinkDef,
    'footer.instagram_link': instagramLinkDef,
    'footer.youtube_link': youtubeLinkDef,
    'footer.weidner_cares_logo': weidnerCaresLogoDef,
    'footer.love_notes_logo': loveNotesLogoDef,
    'footer.text_us_link': textUsLinkDef,
  },
  collectionBindings: {
    'footer.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(Footer as any).contentSchema = contentSchema;

export default Footer;
